import React from 'react';
import {Box} from '@mui/material';
import {NAVY, TEXTE_SECONDAIRE} from '../tokens.js';
import {echelle, troncons} from './courbe.echelle.js';

// Lot 9 : courbe SVG maison (aucune bibliothèque de graphiques), partagée par le dashboard et la fiche.
// `points` : [{x: 'Y-m-d', y: number|null}] ; `null` = trou (la ligne est coupée). Dernière valeur non nulle en étiquette à droite.

const LARGEUR = 100; // unités du viewBox ; le SVG est étiré à 100 % de la largeur (preserveAspectRatio none)

export {echelle};

export default function Courbe({points, hauteur = 56, couleur = NAVY, format = (y) => String(y), cible = null}) {
    const ech = echelle(points || [], cible != null ? [cible] : []);
    if (!ech) {
        return <Box sx={{height: hauteur, display: 'flex', alignItems: 'center', fontSize: 12, color: TEXTE_SECONDAIRE}}>—</Box>;
    }
    const n = points.length;
    const px = (i) => (n === 1 ? LARGEUR / 2 : (i / (n - 1)) * LARGEUR);
    const py = (y) => hauteur - ((y - ech.min) / (ech.max - ech.min)) * hauteur;
    const lignes = troncons(points).map((t) => {
        // Un point isolé devient un trait minuscule (cap rond) pour rester visible.
        const pts = t.length === 1 ? [{i: t[0].i, y: t[0].y, dx: -0.3}, {i: t[0].i, y: t[0].y, dx: 0.3}] : t.map((q) => ({...q, dx: 0}));
        const d = pts.map((q, k) => `${k === 0 ? 'M' : 'L'}${(px(q.i) + q.dx).toFixed(2)} ${py(q.y).toFixed(2)}`).join(' ');
        const aire = `${d} L${(px(pts[pts.length - 1].i) + pts[pts.length - 1].dx).toFixed(2)} ${hauteur} L${(px(pts[0].i) + pts[0].dx).toFixed(2)} ${hauteur} Z`;
        return {d, aire, cle: t[0].i};
    });
    const derniere = [...points].reverse().find((p) => typeof p.y === 'number' && Number.isFinite(p.y));
    const largeurBande = n === 1 ? LARGEUR : LARGEUR / (n - 1);
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', width: '100%'}}>
            <Box component="svg" role="img" aria-label={`Courbe, dernière valeur ${format(derniere.y)}`} viewBox={`0 0 ${LARGEUR} ${hauteur}`} preserveAspectRatio="none" width="100%" height={hauteur} sx={{flex: '1 1 auto', minWidth: 0, display: 'block', overflow: 'visible'}}>
                {cible != null && (
                    <line data-testid="courbe-cible" x1="0" x2={LARGEUR} y1={py(cible)} y2={py(cible)} stroke="#8A929C" strokeWidth="1" strokeDasharray="4 3" vectorEffect="non-scaling-stroke"/>
                )}
                {lignes.map((l) => (
                    <g key={l.cle}>
                        <path d={l.aire} fill={couleur} fillOpacity="0.12" stroke="none"/>
                        <path d={l.d} fill="none" stroke={couleur} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"/>
                    </g>
                ))}
                {points.map((p, i) => (
                    <rect key={p.x + i} x={Math.max(0, px(i) - largeurBande / 2)} y="0" width={largeurBande} height={hauteur} fill="transparent">
                        <title>{`${p.x} · ${typeof p.y === 'number' ? format(p.y) : '—'}`}</title>
                    </rect>
                ))}
            </Box>
            <Box component="span" sx={{flex: '0 0 auto', fontSize: 13, fontWeight: 700, color: NAVY, whiteSpace: 'nowrap'}}>{format(derniere.y)}</Box>
        </Box>
    );
}
